import { DirectionsCarOutlined, Payments, Save, TwoWheeler } from '@mui/icons-material';
import {
	Box,
	FormControl,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { ReactNode } from 'react';
import { maskCurrencyBRL } from '../../../utils/mask/MaskUtils';
import { withAlpha } from '../indicacao.utils';
import { IndicacaoButton } from './IndicacaoButton';
import { SectionHeader } from './SectionHeader';

const CampoComissao = ({
	icon,
	rotulo,
	value,
	disabled,
	onChange,
}: {
	icon: ReactNode;
	rotulo: string;
	value: string;
	disabled: boolean;
	onChange: (value: string) => void;
}) => {
	const theme = useTheme();
	const primary = theme.palette.primary.main;

	return (
		<Box
			sx={{
				p: 2,
				minWidth: 0,
				borderRadius: 2.5,
				border: `1px solid ${withAlpha(primary, 0.22)}`,
				bgcolor: '#fff',
			}}
		>
			<Box display="flex" alignItems="center" gap={1.25} mb={1.5}>
				<Box
					sx={{
						width: 36,
						height: 36,
						borderRadius: 2,
						flexShrink: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: primary,
						bgcolor: withAlpha(primary, 0.12),
					}}
				>
					{icon}
				</Box>
				<Box minWidth={0}>
					<Typography fontWeight={800}>{rotulo}</Typography>
					<Typography fontSize="0.8rem" color="text.secondary">
						por veículo indicado
					</Typography>
				</Box>
			</Box>
			<TextField
				fullWidth
				value={value.replace(/^R\$\s?/, '')}
				disabled={disabled}
				onChange={(event) => onChange(maskCurrencyBRL(event.target.value))}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<Typography fontWeight={800} color="text.secondary">
								R$
							</Typography>
						</InputAdornment>
					),
					sx: { fontWeight: 800, fontSize: '1.35rem' },
				}}
			/>
		</Box>
	);
};

interface CommissionPanelProps {
	valorMoto: string;
	valorOutros: string;
	tipoRegraPadrao: string;
	qtdDias: string;
	qtdVezes: string;
	carregando: boolean;
	salvando: boolean;
	onChangeMoto: (value: string) => void;
	onChangeOutros: (value: string) => void;
	onChangeTipoRegraPadrao: (value: string) => void;
	onChangeQtdDias: (value: string) => void;
	onChangeQtdVezes: (value: string) => void;
	onSalvar: () => void;
}

export function CommissionPanel({
	valorMoto,
	valorOutros,
	tipoRegraPadrao,
	qtdDias,
	qtdVezes,
	carregando,
	salvando,
	onChangeMoto,
	onChangeOutros,
	onChangeTipoRegraPadrao,
	onChangeQtdDias,
	onChangeQtdVezes,
	onSalvar,
}: CommissionPanelProps) {
	return (
		<Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: '1px solid #f1f5f9', height: '100%' }}>
			<SectionHeader
				icon={<Payments />}
				titulo="Comissão"
				descricao="O novo valor entra nos ganhos ainda não calculados. O modo padrão vale só para indicador que ainda não tem regra salva."
				acao={
					<IndicacaoButton
						label={salvando ? 'Salvando...' : 'Salvar'}
						icon={<Save />}
						loading={salvando}
						disabled={carregando}
						onClick={onSalvar}
					/>
				}
			/>
			<Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5} mt={2.5}>
				<CampoComissao
					icon={<TwoWheeler sx={{ fontSize: 20 }} />}
					rotulo="Moto"
					value={valorMoto}
					disabled={carregando || salvando}
					onChange={onChangeMoto}
				/>
				<CampoComissao
					icon={<DirectionsCarOutlined sx={{ fontSize: 20 }} />}
					rotulo="Outros veículos"
					value={valorOutros}
					disabled={carregando || salvando}
					onChange={onChangeOutros}
				/>
			</Box>
			<Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1.2fr 1fr 1fr' }} gap={1.5} mt={1.5}>
				<FormControl fullWidth disabled={carregando || salvando}>
					<InputLabel id="tipo-regra-padrao">Modo padrão</InputLabel>
					<Select
						labelId="tipo-regra-padrao"
						label="Modo padrão"
						value={tipoRegraPadrao}
						onChange={(event) => onChangeTipoRegraPadrao(event.target.value)}
					>
						<MenuItem value="">Sem padrão</MenuItem>
						<MenuItem value="DIAS">Por dias</MenuItem>
						<MenuItem value="VEZES">Pelas primeiras compras</MenuItem>
					</Select>
				</FormControl>
				<TextField
					label="Dias"
					value={qtdDias}
					disabled={carregando || salvando}
					placeholder="Sem limite"
					onChange={(event) => onChangeQtdDias(event.target.value.replace(/\D/g, ''))}
					helperText="Vazio não corta por data"
				/>
				<TextField
					label="Compras"
					value={qtdVezes}
					disabled={carregando || salvando}
					placeholder="Sem limite"
					onChange={(event) => onChangeQtdVezes(event.target.value.replace(/\D/g, ''))}
					helperText="Vazio não corta por quantidade"
				/>
			</Box>
		</Paper>
	);
}
